import React from 'react'
import { Form } from "react-bootstrap"

export function FormField({ field, value, onChange, error }) {
  const handleChange = (e) => {
    const { type, checked, value: inputValue, files, name } = e.target

    // Handle different input types
    if (type === "checkbox") {
      if (field.options) {
        // For checkbox groups, maintain an array of selected values
        const newValue = Array.isArray(value) ? [...value] : []
        if (checked) {
          // Add the value if checked
          if (!newValue.includes(inputValue)) {
            newValue.push(inputValue)
          }
        } else {
          // Remove the value if unchecked
          const index = newValue.indexOf(inputValue)
          if (index !== -1) {
            newValue.splice(index, 1)
          }
        }
        onChange(newValue)
      } else {
        // For single checkbox, just use the checked state
        onChange(checked)
      }
    } else if (type === "file") {
      if (field.multiple) {
        // For multiple file selection, pass the FileList object
        onChange(files)
      } else {
        // For single file selection, pass just the first file
        onChange(files[0])
      }
    } else {
      onChange(inputValue)
    }
  }

  const renderField = () => {
    switch (field.type) {
      // Text inputs
      case "text":
      case "password":
      case "email":
      case "number":
      case "tel":
      case "url":
      case "search":
      case "date":
      case "time":
      case "datetime-local":
      case "month":
      case "week":
        return (
          <Form.Control
            type={field.type}
            placeholder={field.placeholder}
            value={value || ""}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          />
        )

      // Special inputs
      case "color":
        return (
          <Form.Control
            type="color"
            value={value || "#000000"}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          />
        )

      case "range":
        return (
          <>
            <Form.Control
              type="range"
              min={field.min || "0"}
              max={field.max || "100"}
              step={field.step || "1"}
              value={value || "0"}
              onChange={handleChange}
              isInvalid={!!error}
              required={field.required}
            />
            <small className="text-muted">Current value: {value || 0}</small>
          </>
        )

      case "file":
        return (
          <>
            <Form.Control
              type="file"
              onChange={handleChange}
              isInvalid={!!error}
              required={field.required}
              accept={field.accept}
              multiple={field.multiple}
            />
            {field.multiple && value && (
              <small className="text-muted">
                {value.length} file{value.length !== 1 ? "s" : ""} selected
              </small>
            )}
          </>
        )

      case "hidden":
        return <Form.Control type="hidden" value={value || ""} onChange={handleChange} />

      case "textarea":
        return (
          <Form.Control
            as="textarea"
            rows={field.rows || 3}
            placeholder={field.placeholder}
            value={value || ""}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          />
        )

      case "select":
        return (
          <Form.Control
            as="select"
            value={value || ""}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          >
            <option value="">{field.placeholder || "Select an option"}</option>
            {field.options &&
              field.options.map((option, index) => {
                // Support both simple strings and {label, value} objects
                const optionLabel = typeof option === "object" ? option.label : option
                const optionValue = typeof option === "object" ? option.value : option

                return (
                  <option key={index} value={optionValue}>
                    {optionLabel}
                  </option>
                )
              })}
          </Form.Control>
        )

      case "checkbox":
        if (field.options && field.options.length > 0) {
          // Multiple checkboxes (checkbox group)
          return (
            <div>
              {field.options.map((option, index) => {
                // Support both simple strings and {label, value} objects
                const optionLabel = typeof option === "object" ? option.label : option
                const optionValue = typeof option === "object" ? option.value : option
                const isChecked = Array.isArray(value) && value.includes(optionValue)

                return (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`${field.id}-checkbox-${index}`}
                    label={optionLabel}
                    value={optionValue}
                    checked={isChecked}
                    onChange={handleChange}
                    isInvalid={!!error}
                    required={field.required && index === 0} // Only mark first checkbox as required
                  />
                )
              })}
            </div>
          )
        } else {
          // Single checkbox
          return (
            <Form.Check
              type="checkbox"
              id={`${field.id}-checkbox`}
              label={field.checkboxLabel || field.label}
              checked={value || false}
              onChange={handleChange}
              isInvalid={!!error}
              required={field.required}
            />
          )
        }

      case "radio":
        return (
          <div>
            {field.options &&
              field.options.map((option, index) => {
                // Support both simple strings and {label, value} objects
                const optionLabel = typeof option === "object" ? option.label : option
                const optionValue = typeof option === "object" ? option.value : option

                return (
                  <Form.Check
                    key={index}
                    type="radio"
                    id={`${field.id}-radio-${index}`}
                    label={optionLabel}
                    name={field.id}
                    value={optionValue}
                    checked={value === optionValue}
                    onChange={handleChange}
                    isInvalid={!!error}
                    required={field.required && index === 0} // Only mark first radio as required
                  />
                )
              })}
          </div>
        )

      default:
        return (
          <Form.Control
            type="text"
            placeholder={field.placeholder}
            value={value || ""}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          />
        )
    }
  }

  return (
    <Form.Group className="mb-1">
      {(field.type !== "checkbox" || field.options) && field.type !== "hidden" && (
        <Form.Label>
          {field.label}
          {field.required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      {renderField()}
      {field.helpText && !error && <Form.Text className="text-muted">{field.helpText}</Form.Text>}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  )
}
